import { Button, Card, Collapse, Form, InputNumber, Modal, Popconfirm, Select, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CaretRightOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { TemplateAdministrationInstitutionVehicle } from "../../Models";
import usePlacementAgentsAdmin from "../../Hooks/usePlacementAgentsAdmin/usePlacementAgentsAdmin";

import { IPlacementAgentsAdminProps } from "./IPlacementAgentsAdmin";

const PlacementAgentsAdmin = ({
	loading,
	isReadOnly,
	translations,
	institutions,
	vehicles,
	institutionVehicles,
	handleSaveInstitutionVehicle,
	handleDeleteInstitutionVehicle
}: IPlacementAgentsAdminProps) => {
	const t = (key: string, fallback: string) => translations[key] || fallback;
	const afinLabel = t("adminAfinLabel", "Habilitar envío a Afin");
	const crsLabel = t("adminCRSLabel", "Habilitar paso CRS");
	const fatcaLabel = t("adminFATCALabel", "Habilitar paso FATCA");
	const getSuitabilityTestTypeLabel = (type: number) => {
		switch (type) {
			case 1:
				return t("adminSuitabilityTestTypeStandard", "Estándar");
			case 2:
				return t("adminSuitabilityTestTypePortugal", "Perfil inversor PT");
			default:
				return t("adminSuitabilityTestTypeNone", "No aplica");
		}
	};
	const suitabilityTestTypeOptions = [
		{ value: 0, label: getSuitabilityTestTypeLabel(0) },
		{ value: 1, label: getSuitabilityTestTypeLabel(1) },
		{ value: 2, label: getSuitabilityTestTypeLabel(2) }
	];
	const {
		selectedInstitutionId,
		setSelectedInstitutionId,
		selectedVehicleId,
		setSelectedVehicleId,
		setExpandedRowKeys,
		drafts,
		isModalOpen,
		setIsModalOpen,
		form,
		groupedInstitutionVehicles,
		filteredExpandedRowKeys,
		updateDraft,
		openModal,
		handleCreate,
		formatThousands,
		parseThousands
	} = usePlacementAgentsAdmin({
		institutionVehicles,
		handleSaveInstitutionVehicle
	});

	const columns: ColumnsType<TemplateAdministrationInstitutionVehicle> = [
		{
			title: t("institution", "Institución"),
			key: "institution",
			render: (_, record) => (
				<div className="templates-details-cell">
					<div className="templates-details-summary">{record.vehicleName}</div>
					<div className="templates-details-muted">{record.institutionName}</div>
				</div>
			)
		},
		{
			title: t("details", "Detalles"),
			key: "details",
			render: (_, record) => {
				const draft = drafts[`${record.idInstitution}_${record.idVehicle}`];
				const enabledFlags = [
					draft?.afin && afinLabel,
					draft?.includesSuitabilityTest && `${t("adminSuitabilityTestLabel", "Tipo de test MiFID")} (${getSuitabilityTestTypeLabel(draft?.suitabilityTestType ?? 0)})`,
					draft?.includesInvestorStatus && t("adminInvestorStatusLabel", "Habilitar paso Estado del inversor"),
					draft?.includesRepresentativesPF && t("adminRepresentativesPFLabel", "Habilitar Representantes en persona física"),
					draft?.includesInvestorWithoutNIF && t("adminInvestorWithoutNifLabel", "Mostrar sección Inversor sin NIF en Formulario del Inversor"),
					draft?.includesCRS && crsLabel,
					draft?.includesFATCA && fatcaLabel,
					draft?.includesBeneficiariesGreater5 && t("adminBeneficiariesGreater5Label", "Habilitar paso Beneficiarios >= 5%"),
					draft?.includesRegistrationNumber && t("adminRegistrationNumberLabel", "Mostrar número de registro en Formulario del Inversor")
				].filter(Boolean);

				return (
					<div className="templates-details-cell">
						<div className="templates-details-summary">
							{t("adminMaxAvailable", "Máximo disponible")}: {formatThousands(draft?.maxAvailable ?? 0)}
						</div>
						<div className="templates-details-muted">
							{t("adminActive", "Activo")}: {(draft?.active ?? false) ? t("si", "Sí") : t("no", "No")} · {t("details", "Detalles")}: {enabledFlags.length > 0 ? enabledFlags.join(", ") : "-"}
						</div>
					</div>
				);
			}
		}
	];

	return (
		<>
			<Card
				className="templates-static-card"
				title={t("adminPlacementAgentsTitle", "Agentes colocadores")}
				loading={loading}
			>
				<div className="institution-vehicle-toolbar">
					<div className="institution-vehicle-input-group">
						<div className="institution-vehicle-input-label">
							{t("adminInstitutionFilter", "Filtrar por institución")}
						</div>
						<Select
							value={selectedInstitutionId ?? undefined}
							onChange={setSelectedInstitutionId}
							placeholder={t("adminSelectInstitution", "Filtrar por institución")}
							allowClear
							showSearch
							optionFilterProp="label"
							options={institutions.map((institution) => ({
								value: institution.id,
								label: institution.name
							}))}
						/>
					</div>
					<div className="institution-vehicle-input-group placement-agents-vehicle-filter">
						<div className="institution-vehicle-input-label">
							{t("adminVehicleFilter", "Filtrar por vehículo")}
						</div>
						<Select
							value={selectedVehicleId ?? undefined}
							onChange={setSelectedVehicleId}
							placeholder={t("adminSelectVehicle", "Filtrar por vehículo")}
							allowClear
							showSearch
							optionFilterProp="label"
							options={vehicles.map((vehicle) => ({
								value: vehicle.id,
								label: vehicle.name
							}))}
						/>
					</div>
					<div className="institution-vehicle-toolbar-action">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							disabled={isReadOnly}
							onClick={openModal}
						>
							{t("add", "Añadir")}
						</Button>
					</div>
				</div>

				<Collapse
					className="template-fields-sections"
					items={groupedInstitutionVehicles.map((group) => ({
						key: group.idInstitution,
						label: `${group.institutionName} (${group.rows.length})`,
						children: (
							<Table
								rowKey={(record) => `${record.idInstitution}_${record.idVehicle}`}
								className="templates-table"
								columns={columns}
								dataSource={group.rows}
								pagination={false}
								expandable={{
									expandedRowKeys: filteredExpandedRowKeys,
									onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
									expandRowByClick: true,
									expandIcon: ({ expanded, onExpand, record }) => (
										<Button
											type="text"
											size="small"
											icon={<CaretRightOutlined rotate={expanded ? 90 : 0} />}
											onClick={(event) => onExpand(record, event)}
										/>
									),
									expandedRowRender: (record) => {
										const draft = drafts[`${record.idInstitution}_${record.idVehicle}`];
										if (!draft) {
											return null;
										}

										const renderSwitch = (label: string, field: keyof typeof draft) => (
											<div className="institution-vehicle-flag">
												<span>{label}</span>
												<Switch
													checked={Boolean(draft[field])}
													onChange={(checked) => updateDraft(record.idInstitution, record.idVehicle, field, checked)}
													disabled={isReadOnly}
												/>
											</div>
										);

										return (
											<div className="institution-vehicle-expanded">
												<div className="institution-vehicle-expanded-main">
													<div className="institution-vehicle-number">
														<span>{t("adminMaxAvailable", "Máximo disponible")}</span>
														<InputNumber<number>
															min={0}
															controls={false}
															value={draft.maxAvailable}
															onChange={(value) => updateDraft(record.idInstitution, record.idVehicle, "maxAvailable", Number(value) || 0)}
															disabled={isReadOnly}
															formatter={(value) => formatThousands(value)}
															parser={(value) => parseThousands(value)}
														/>
													</div>
													<div className="institution-vehicle-flag">
														<span>{t("adminActive", "Activo")}</span>
														<Switch
															checked={draft.active}
															onChange={(checked) => updateDraft(record.idInstitution, record.idVehicle, "active", checked)}
															disabled={isReadOnly}
														/>
													</div>
												</div>
												<div className="institution-vehicle-flags">
													{renderSwitch(afinLabel, "afin")}
										<div className="institution-vehicle-flag institution-vehicle-flag-wide">
											<span>{t("adminSuitabilityTestLabel", "Tipo de test MiFID")}</span>
											<Select
												value={draft.suitabilityTestType ?? 0}
												onChange={(value) => {
													updateDraft(record.idInstitution, record.idVehicle, "suitabilityTestType", value);
													updateDraft(record.idInstitution, record.idVehicle, "includesSuitabilityTest", value > 0);
															}}
															disabled={isReadOnly}
															options={suitabilityTestTypeOptions}
															style={{ minWidth: 220 }}
														/>
													</div>
													{renderSwitch(t("adminInvestorStatusLabel", "Habilitar paso Estado del inversor"), "includesInvestorStatus")}
													{renderSwitch(t("adminRepresentativesPFLabel", "Habilitar Representantes en persona física"), "includesRepresentativesPF")}
													{renderSwitch(t("adminInvestorWithoutNifLabel", "Mostrar sección Inversor sin NIF en Formulario del Inversor"), "includesInvestorWithoutNIF")}
													{renderSwitch(crsLabel, "includesCRS")}
													{renderSwitch(fatcaLabel, "includesFATCA")}
													{renderSwitch(t("adminBeneficiariesGreater5Label", "Habilitar paso Beneficiarios >= 5%"), "includesBeneficiariesGreater5")}
													{renderSwitch(t("adminRegistrationNumberLabel", "Mostrar número de registro en Formulario del Inversor"), "includesRegistrationNumber")}
												</div>
												<div className="institution-vehicle-actions">
													<Button
														type="primary"
														onClick={() => handleSaveInstitutionVehicle(draft)}
														disabled={isReadOnly}
													>
														{t("save", "Guardar")}
													</Button>
													<Popconfirm
														title={t("adminDeleteTitle", "Eliminar")}
														description={t("adminDeleteInstitutionVehicleConfirm", "¿Eliminar esta relación institución-vehículo?")}
														onConfirm={() => handleDeleteInstitutionVehicle(record.idInstitution, record.idVehicle)}
														okText={t("accept", "Aceptar")}
														cancelText={t("cancel", "Cancelar")}
														disabled={isReadOnly}
													>
														<Button
															danger
															icon={<DeleteOutlined />}
															disabled={isReadOnly}
														>
															{t("delete", "Eliminar")}
														</Button>
													</Popconfirm>
												</div>
											</div>
										);
									}
								}}
							/>
						)
					}))}
				/>
			</Card>

			<Modal
				width={960}
				title={t("adminPlacementAgentsNewTitle", "Nueva relación institución-vehículo")}
				open={isModalOpen}
				onOk={handleCreate}
				onCancel={() => setIsModalOpen(false)}
				okText={t("add", "Añadir")}
				cancelText={t("cancel", "Cancelar")}
				destroyOnClose
			>
				<Form
					className="placement-agents-create-form"
					layout="vertical"
					labelWrap
					form={form}
					initialValues={{
						active: true,
						afin: false,
						maxAvailable: 0,
						includesSuitabilityTest: false,
						suitabilityTestType: 0,
						includesInvestorStatus: false,
						includesRepresentativesPF: false,
						includesInvestorWithoutNIF: false,
						includesCRS: false,
						includesFATCA: false,
						includesBeneficiariesGreater5: false,
						includesRegistrationNumber: false
					}}
				>
					<Form.Item
						className="placement-agents-create-form-selector"
						label={t("institution", "Institución")}
						name="idInstitution"
						rules={[{ required: true, message: t("adminInstitutionRequired", "La institución es obligatoria") }]}
					>
						<Select
							showSearch
							optionFilterProp="label"
							options={institutions.map((institution) => ({
								value: institution.id,
								label: institution.name
							}))}
						/>
					</Form.Item>
					<Form.Item
						className="placement-agents-create-form-selector"
						label={t("vehicle", "Vehículo")}
						name="idVehicle"
						rules={[{ required: true, message: t("adminVehicleRequired", "El vehículo es obligatorio") }]}
					>
						<Select
							showSearch
							optionFilterProp="label"
							options={vehicles.map((vehicle) => ({
								value: vehicle.id,
								label: vehicle.name
							}))}
						/>
					</Form.Item>
					<Form.Item
						label={t("adminMaxAvailable", "Máximo disponible")}
						name="maxAvailable"
						rules={[{ required: true, message: t("adminMaxAvailableRequired", "El máximo disponible es obligatorio") }]}
					>
						<InputNumber<number>
							min={0}
							controls={false}
							style={{ width: "100%" }}
							formatter={(value) => formatThousands(value)}
							parser={(value) => parseThousands(value)}
						/>
					</Form.Item>
					<Form.Item label={t("adminActive", "Activo")} name="active" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={afinLabel} name="afin" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item name="includesSuitabilityTest" valuePropName="checked" hidden>
						<Switch />
					</Form.Item>
					<Form.Item label={t("adminSuitabilityTestLabel", "Tipo de test MiFID")} name="suitabilityTestType">
						<Select
							defaultValue={0}
							options={suitabilityTestTypeOptions}
							onChange={(value) => form.setFieldValue("includesSuitabilityTest", value > 0)}
						/>
					</Form.Item>
					<Form.Item label={t("adminInvestorStatusLabel", "Habilitar paso Estado del inversor")} name="includesInvestorStatus" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={t("adminRepresentativesPFLabel", "Habilitar Representantes en persona física")} name="includesRepresentativesPF" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={t("adminInvestorWithoutNifLabel", "Mostrar sección Inversor sin NIF en Formulario del Inversor")} name="includesInvestorWithoutNIF" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={crsLabel} name="includesCRS" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={fatcaLabel} name="includesFATCA" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={t("adminBeneficiariesGreater5Label", "Habilitar paso Beneficiarios >= 5%")} name="includesBeneficiariesGreater5" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label={t("adminRegistrationNumberLabel", "Mostrar número de registro en Formulario del Inversor")} name="includesRegistrationNumber" valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default PlacementAgentsAdmin;
